/**
 * GovernedCalendarPanel — the visual, GOVERNED content calendar.
 *
 * Thin over the pure engine in lib/content/schedule: every button here maps
 * to a real state transition, and the core invariant is enforced by the
 * engine, not the view — 'Schedule' opens the approval diff (it can never
 * publish directly), Approve seals provenance via the shipped provenance
 * module, Reverse emits a recorded reversal, and published outcomes are
 * attributed back to the asset as value-ledger entries. Typefully-style
 * queue, ARGO-grade trust layer.
 */

import { useMemo, useState } from 'react';
import {
  CalendarDays,
  Check,
  Clock,
  Megaphone,
  Rocket,
  ShieldCheck,
  Undo2,
  X as XIcon,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  bestTimeToPost,
  buildQueue,
  proposeSchedule,
  approveSchedule,
  reverseSchedule,
  publishPiece,
  campaignMission,
  attributeOutcome,
  upsertPiece,
  type Channel,
  type ContentValueEntry,
  type ScheduleApproval,
  type ScheduleReversalRecord,
  type ScheduleStatus,
  type ScheduledPiece,
} from '../lib/content/schedule';
import {
  buildProvenance,
  deepLinkFor,
  provenanceDigest,
} from '../lib/agent/provenance';

const CHANNELS: Channel[] = ['linkedin', 'x', 'email', 'blog'];

const CHANNEL_LABEL: Record<Channel, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  email: 'Email',
  blog: 'Blog',
};

/** Status → badge accent, one place so badges never drift. */
const STATUS_COLOR: Record<ScheduleStatus, string> = {
  draft: C.textMuted,
  pending_approval: C.orange,
  approved: C.blueLight,
  scheduled: C.blueElectric,
  published: C.green,
  reversed: C.orangeWarm,
};

const STATUS_LABEL: Record<ScheduleStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
  reversed: 'Reversed',
};

function fmtSlot(ts: number): string {
  return new Date(ts).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: ScheduleStatus }) {
  const color = STATUS_COLOR[status];
  return (
    <span
      className="glass-pill"
      style={{
        fontSize: 10,
        padding: '2px 8px',
        color,
        background: `${color.startsWith('#') ? color + '22' : 'rgba(255,255,255,0.06)'}`,
        boxShadow: color.startsWith('#') ? `inset 0 0 0 1px ${color}55` : undefined,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/** Provenance seal for an approved slot — the SAME FNV-1a digest family the
 *  ledger chain verifier uses, via the shipped provenance module. */
function sealSlot(payload: {
  pieceId: string;
  approvalId: string;
  assetId: string;
  channel: Channel;
  publishAt: number;
}): string {
  return provenanceDigest(
    buildProvenance({
      evidence: [
        {
          kind: 'deliverable',
          id: payload.assetId,
          label: `Content asset ${payload.assetId}`,
          deepLink: deepLinkFor('deliverable', payload.assetId),
        },
      ],
      confidence: {
        score: 0.9,
        basis: `Operator approved ${payload.channel} slot ${new Date(payload.publishAt).toISOString()} (approval ${payload.approvalId}).`,
      },
    }),
  );
}

export interface GovernedCalendarPanelProps {
  /** The asset currently composed in the view (id + human title). */
  asset: { id: string; title: string };
  /** Campaign brief seed — the view's topic/brief input, may be empty. */
  brief: string;
}

export function GovernedCalendarPanel({ asset, brief }: GovernedCalendarPanelProps) {
  const [pieces, setPieces] = useState<ScheduledPiece[]>([]);
  const [approvals, setApprovals] = useState<Record<string, ScheduleApproval>>({});
  /** Approval the operator is currently reviewing (the diff card). */
  const [reviewing, setReviewing] = useState<ScheduleApproval | null>(null);
  const [channel, setChannel] = useState<Channel>('linkedin');
  const [reversals, setReversals] = useState<ScheduleReversalRecord[]>([]);
  const [valueEntries, setValueEntries] = useState<ContentValueEntry[]>([]);
  const [missionNote, setMissionNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queue = useMemo(() => buildQueue(pieces), [pieces]);
  const days = useMemo(() => Object.keys(queue.byDay).sort(), [queue]);
  const now = Date.now();

  const enqueue = (ap: ScheduleApproval) =>
    setApprovals((prev) => ({ ...prev, [ap.id]: ap }));

  const upsert = (piece: ScheduledPiece) =>
    setPieces((prev) => upsertPiece(prev, piece));

  /** 'Schedule' NEVER publishes — it proposes, then opens the approval diff. */
  const handlePropose = () => {
    setError(null);
    try {
      const { piece, approval } = proposeSchedule(asset, channel, {
        now: Date.now(),
        enqueue,
      });
      upsert(piece);
      setReviewing(approval);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not propose schedule.');
    }
  };

  const handleApprove = (approval: ScheduleApproval) => {
    setError(null);
    const piece = pieces.find((p) => p.id === approval.pieceId);
    if (!piece) return;
    try {
      upsert(approveSchedule(piece, approval, { seal: sealSlot }));
      setApprovals((prev) => {
        const next = { ...prev };
        delete next[approval.id];
        return next;
      });
      setReviewing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed.');
    }
  };

  /** A rejected proposal never happened — the piece leaves the calendar. */
  const handleReject = (approval: ScheduleApproval) => {
    setPieces((prev) => prev.filter((p) => p.id !== approval.pieceId));
    setApprovals((prev) => {
      const next = { ...prev };
      delete next[approval.id];
      return next;
    });
    setReviewing(null);
  };

  const handleReverse = (piece: ScheduledPiece) => {
    setError(null);
    try {
      upsert(
        reverseSchedule(piece, {
          reverse: (record) => setReversals((prev) => [record, ...prev]),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reversal failed.');
    }
  };

  const handlePublish = (piece: ScheduledPiece) => {
    setError(null);
    try {
      upsert(publishPiece(piece));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed.');
    }
  };

  const handleOutcome = (piece: ScheduledPiece, signals: { replies?: number; meetings?: number }) => {
    attributeOutcome(piece, signals, {
      record: (entry) => setValueEntries((prev) => [entry, ...prev]),
    });
  };

  /** One mission, four per-asset approvals — each still needs a human. */
  const handleLaunchCampaign = () => {
    setError(null);
    const campaignBrief = brief.trim() || asset.title;
    const { pieces: missionPieces, mission } = campaignMission(
      campaignBrief,
      CHANNELS.map((ch) => ({
        channel: ch,
        asset: { id: `${asset.id}-${ch}`, title: `${asset.title} (${CHANNEL_LABEL[ch]})` },
      })),
      { now: Date.now(), enqueue },
    );
    setPieces((prev) => missionPieces.reduce(upsertPiece, prev));
    setMissionNote(`${mission.title} — ${missionPieces.length} pieces awaiting approval`);
  };

  const pendingApprovals = Object.values(approvals).sort(
    (a, b) => a.createdAt - b.createdAt,
  );

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <CalendarDays size={16} style={{ color: C.blueElectric }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Governed calendar
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          every slot needs your approval · sealed · reversible
        </span>
        <span
          className="glass-pill"
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '3px 10px',
            color: C.green,
            background: 'rgba(34,197,94,0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <ShieldCheck size={11} />
          no direct publish
        </span>
      </div>

      {/* Best-time suggestions + schedule action */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {CHANNELS.map((ch) => {
          const active = ch === channel;
          return (
            <button
              key={ch}
              onClick={() => setChannel(ch)}
              className="glass-pill"
              title={`Best time: ${fmtSlot(bestTimeToPost(ch, now))}`}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                cursor: 'pointer',
                border: 'none',
                color: active ? '#fff' : C.textMuted,
                background: active ? 'rgba(0,130,243,0.25)' : 'rgba(255,255,255,0.05)',
                boxShadow: active ? `inset 0 0 0 1px ${C.blueElectric}77` : undefined,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <Clock size={11} />
              {CHANNEL_LABEL[ch]} · {fmtSlot(bestTimeToPost(ch, now))}
            </button>
          );
        })}
        <button
          onClick={handlePropose}
          className="glass-btn glass-btn--primary"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title="Proposes the slot and opens the approval diff — never publishes directly"
        >
          <CalendarDays size={13} />
          Schedule “{asset.title}”
        </button>
        <button
          onClick={handleLaunchCampaign}
          className="glass-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title="One mission, four channel pieces — each needs its own approval"
        >
          <Rocket size={13} />
          Launch campaign
        </button>
      </div>

      {missionNote && (
        <div className="t-meta" style={{ color: C.blueLight, display: 'flex', gap: 6 }}>
          <Megaphone size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{missionNote}</span>
        </div>
      )}

      {error && (
        <div className="t-meta" style={{ color: C.orangeWarm }}>
          {error}
        </div>
      )}

      {/* Approval diff card — the human gate. */}
      {(reviewing || pendingApprovals.length > 0) && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="t-eyebrow" style={{ color: C.orange }}>
            Awaiting your approval ({pendingApprovals.length})
          </div>
          {pendingApprovals.map((ap) => (
            <div
              key={ap.id}
              style={{
                border: `1px solid ${C.orange}44`,
                background: 'rgba(253,173,1,0.06)',
                borderRadius: 10,
                padding: 12,
                display: 'grid',
                gap: 8,
              }}
            >
              <div className="t-meta" style={{ color: C.text }}>
                {ap.note}
              </div>
              <code
                style={{
                  fontSize: 11,
                  color: C.textMuted,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 6,
                  padding: '6px 8px',
                }}
              >
                {ap.actionDiff}
              </code>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleApprove(ap)}
                  className="glass-btn glass-btn--primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <Check size={13} /> Approve &amp; seal
                </button>
                <button
                  onClick={() => handleReject(ap)}
                  className="glass-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <XIcon size={13} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Conflicts */}
      {queue.conflicts.length > 0 && (
        <div className="t-meta" style={{ color: C.orange }}>
          ⚠ {queue.conflicts.length} slot conflict
          {queue.conflicts.length === 1 ? '' : 's'}:{' '}
          {queue.conflicts
            .map((g) => `${CHANNEL_LABEL[g[0].channel]} ${fmtSlot(g[0].publishAt)}`)
            .join(' · ')}
        </div>
      )}

      {/* Calendar queue */}
      {days.length === 0 ? (
        <div
          className="t-meta"
          style={{
            color: C.textMuted,
            border: '1px dashed rgba(255,255,255,0.15)',
            borderRadius: 10,
            padding: '18px 14px',
            textAlign: 'center',
          }}
        >
          Nothing slotted yet — pick a channel above and hit Schedule. Every slot
          routes through your approval before it can ever publish.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(days.length, 4)}, minmax(0, 1fr))`,
            gap: 10,
          }}
        >
          {days.map((day) => (
            <div key={day} style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
              <div className="t-eyebrow" style={{ color: C.textDim }}>
                {new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
                })}
              </div>
              {queue.byDay[day].map((p) => (
                <div
                  key={p.id}
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10,
                    padding: 10,
                    display: 'grid',
                    gap: 6,
                    background: 'rgba(255,255,255,0.03)',
                    opacity: p.status === 'reversed' ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="t-card-title" style={{ fontSize: 12, color: C.text, flex: 1 }}>
                      {CHANNEL_LABEL[p.channel]}
                    </span>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>
                    {fmtSlot(p.publishAt)} · {p.assetId}
                    {p.sealId && (
                      <span title={`Provenance seal ${p.sealId}`}> · sealed</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.status === 'scheduled' && (
                      <button
                        onClick={() => handlePublish(p)}
                        className="glass-btn"
                        style={{ padding: '3px 8px', fontSize: 11 }}
                      >
                        Mark published
                      </button>
                    )}
                    {(p.status === 'scheduled' || p.status === 'published') && (
                      <button
                        onClick={() => handleReverse(p)}
                        className="glass-btn"
                        style={{
                          padding: '3px 8px',
                          fontSize: 11,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          color: C.orangeWarm,
                        }}
                        title="Emits a recorded reversal — undo is never silent"
                      >
                        <Undo2 size={11} /> Reverse
                      </button>
                    )}
                    {p.status === 'published' && (
                      <>
                        <button
                          onClick={() => handleOutcome(p, { replies: 1 })}
                          className="glass-btn"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          title="Attribute a reply back to this asset in the value ledger"
                        >
                          + reply
                        </button>
                        <button
                          onClick={() => handleOutcome(p, { meetings: 1 })}
                          className="glass-btn"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          title="Attribute a booked meeting back to this asset"
                        >
                          + meeting
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Reversal + ROI ledgers — honest history, never hidden. */}
      {(reversals.length > 0 || valueEntries.length > 0) && (
        <div style={{ display: 'grid', gap: 4, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
          {reversals.map((r) => (
            <div key={r.id} className="t-meta" style={{ color: C.orangeWarm, fontSize: 11 }}>
              {r.note}
            </div>
          ))}
          {valueEntries.map((v, i) => (
            <div key={`${v.id}-${i}`} className="t-meta" style={{ color: C.green, fontSize: 11 }}>
              {v.note} (~{v.minutesSaved} min of pipeline value)
            </div>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}
